import { Injectable } from '@angular/core';
import { cloneDeep, forEach, isEqual, omit, omitBy } from 'lodash-es';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticationService, UserService } from '../../core/services';
import { AccountAttributesEnum } from '../enums';
import { ContactSearch, UniversalSearchRequestModelInterface } from '../../core/models';
import { BaseApiService } from './api';
import { SavedSearchCreateUpdateResponse, SaveSearchItem } from '../dto';

@Injectable({
    providedIn: 'root'
})
export class SavedSearches {
    welComeLoadedFlag = new BehaviorSubject(false);
    welComeLoadedCompanyFlag = new BehaviorSubject(false);
    companyActiveSearch;
    contactActiveSearch;
    universalActiveSearch: SavedSearchCreateUpdateResponse<UniversalSearchRequestModelInterface>;
    currentCompanySearchId = '';
    currentContactSearchId = '';
    currentSearchId = null;
    currentSearchName = '';
    currentSearchIdVisitorIntel = '';
    currentSearchNameVisitorIntel = '';
    saveSearchData;
    private saveSearchSubject: BehaviorSubject<SaveSearchItem[]> = new BehaviorSubject([]);
    readonly ignoreFieldsArray = ['required_fields_all', 'sort_direction', 'sort_by', 'tiers', 'page', 'technographics', 'location_types', 'last_funding_rounds', 'min_last_funding_date', 'max_last_funding_date', 'min_total_funding_amount', 'max_total_funding_amount', 'min_last_funding_amount', 'max_last_funding_amount'];
    private welcomePageSavedSearch: Subject<void> = new Subject<void>();
    private saveUpdateSearchModalClosed = new Subject<void>();
    private url: string = 'service/contactcloud/search/savedSearch';
    refresh$: Observable<symbol>;
    private readonly refreshSubject: Subject<symbol> = new Subject();

    constructor(
        private auth: AuthenticationService,
        private userService: UserService,
        private readonly baseApiService: BaseApiService,

    ) {
        this.refresh$ = this.refreshSubject.asObservable();
    }

    getSaveUpdateSearchModalClosed(): Observable<void> {
        return this.saveUpdateSearchModalClosed.asObservable();
    }

    emitSaveUpdateSearchModalClosed(): void {
        this.saveUpdateSearchModalClosed.next();
    }

    getExternalSavedSearchEvent(): Observable<void> {
        return this.welcomePageSavedSearch.asObservable();
    }

    emitExternalSavedSearchEvent() {
        this.welcomePageSavedSearch.next();
    }

    setSavedSearch(data: SaveSearchItem[]) {
        this.saveSearchSubject.next(data);
    }

    getSavedSearchList() {
        return this.saveSearchSubject.asObservable();
    }

    getSavedSearchById(id) {
        return this.baseApiService.get(`${this.url}/${id}`, {showError: true}).pipe(
            map((response) => this.handleGetSavedSearchByIdResponse(response))
        );
    }

    getSavedSearchByIdHttp(id: string) {
        return this.baseApiService.get(`service/contactcloud/search/savedSearch/${id}`, { showError: true }).pipe(
            map((response) => this.handleGetSavedSearchByIdResponse(response))
        );
    }

    // TODO: refactor
    handleGetSavedSearchByIdResponse(response) {
        if (response?.['query']) {
            if (!response?.['query']?.['accuracy_tier_company']) {
                response['query']['accuracy_tier_company'] = 'MV';
            }
            if (!response?.['query']?.['accuracy_tier_contact']){
                response['query']['accuracy_tier_contact'] = 'EV';
            }
            if (!response?.['query']?.['required_fields']) {
                response['query']['required_fields'] = cloneDeep(ContactSearch.required_fields);
            }

            const companyLocationStates = response?.['query']?.['company_location_states'];
            if (Array.isArray(companyLocationStates) && typeof companyLocationStates[0] === 'object') {
                response['query'] = { ...response['query'], ...{company_location_states: companyLocationStates.map((el) => el.abbreviation)} }
            }

            if (response['query']?.intent_minimum_score && !response['query']?.intent_maximum_score) {
                response['query'].intent_maximum_score = 100;
            }
        }

        return response;
    }

    setUniversalActiveSearch(search: SavedSearchCreateUpdateResponse<UniversalSearchRequestModelInterface>) {
        this.universalActiveSearch = cloneDeep(search);
    }

    updateSavedSearchNameHttp(itemId, information): Observable<SaveSearchItem> {
        return this.baseApiService.put(`service/contactcloud/search/savedSearch/${itemId}`, { information }, { showError: true });
    }

    setUniversalActiveSearchLock(lock: boolean): void {
        if (this.universalActiveSearch) {
            this.universalActiveSearch.lock = lock;
        }
    }

    isCompanyActiveSearchChange(currentQuery) {
        if (this.companyActiveSearch) {
            const currentPrunedCriteria = this.omitEmptyProps(cloneDeep(currentQuery));
            if (typeof this.companyActiveSearch.query.tiers === 'object') {
                if (this.companyActiveSearch.query.tiers['humanverified']) {
                    this.companyActiveSearch.query.tiers = '0';
                } else {
                    this.companyActiveSearch.query.tiers = this.auth.getPendingTiers().toString();
                }
            }
            const comparisionPrunedCriteria = this.omitEmptyProps(cloneDeep(this.companyActiveSearch.query));
            return isEqual(omit(this.pruneEmptyValues(JSON.parse(
                JSON.stringify(currentPrunedCriteria))), this.ignoreFieldsArray), omit(this.pruneEmptyValues(
                    JSON.parse(JSON.stringify(comparisionPrunedCriteria))), this.ignoreFieldsArray));
        } else if (this.universalActiveSearch) {
            const currentPrunedCriteria = this.omitEmptyProps(cloneDeep(currentQuery));
            if (typeof this.universalActiveSearch.query.tiers === 'object') {
                if (this.universalActiveSearch.query.tiers['humanverified']) {
                    this.universalActiveSearch.query.tiers = '0';
                } else {
                    this.universalActiveSearch.query.tiers = this.auth.getPendingTiers().toString();
                }
            }
            const comparisionPrunedCriteria = this.omitEmptyProps(this.addDefaultAccuracyTier(this.universalActiveSearch.query, this.universalActiveSearch.type));
            return isEqual(omit(this.pruneEmptyValues(JSON.parse(
                JSON.stringify(currentPrunedCriteria))), this.ignoreFieldsArray), omit(this.pruneEmptyValues(
                    JSON.parse(JSON.stringify(comparisionPrunedCriteria))), this.ignoreFieldsArray));
        } else {
            return false;
        }
    }

    isContactActiveSearchChange(currentQuery) {
        if (this.contactActiveSearch) {
            const currentPrunedCriteria = this.omitEmptyProps(cloneDeep(currentQuery));
            if (typeof this.contactActiveSearch.query.tiers === 'object') {
                if (this.contactActiveSearch.query.tiers['humanverified']) {
                    this.contactActiveSearch.query.tiers = '0';
                } else {
                    this.contactActiveSearch.query.tiers = this.auth.getPendingTiers().toString();
                }
            }
            if (this.contactActiveSearch.query.company_naics_codes && Array.isArray(this.contactActiveSearch.query.company_naics_codes)) {
                this.contactActiveSearch.query.company_naics_codes = this.contactActiveSearch.query.company_naics_codes.join(',');
            }
            if (currentPrunedCriteria.company_naics_codes && Array.isArray(currentPrunedCriteria.company_naics_codes)) {
                currentPrunedCriteria.company_naics_codes = currentPrunedCriteria.company_naics_codes.join(',');
            }
            const comparisionPrunedCriteria = this.omitEmptyProps(cloneDeep(this.contactActiveSearch.query));
            return isEqual(omit(this.pruneEmptyValues(JSON.parse(
                JSON.stringify(currentPrunedCriteria))), this.ignoreFieldsArray), omit(this.pruneEmptyValues(
                    JSON.parse(JSON.stringify(comparisionPrunedCriteria))), this.ignoreFieldsArray));
        } else if (this.universalActiveSearch) {
            const currentPrunedCriteria = this.omitEmptyProps(cloneDeep(currentQuery));
            if (typeof this.universalActiveSearch.query.tiers === 'object') {
                if (this.universalActiveSearch.query.tiers['humanverified']) {
                    this.universalActiveSearch.query.tiers = '0';
                } else {
                    this.universalActiveSearch.query.tiers = this.auth.getPendingTiers().toString();
                }
            }
            if (this.universalActiveSearch.query.company_naics_codes && Array.isArray(this.universalActiveSearch.query.company_naics_codes)) {
                this.universalActiveSearch.query.company_naics_codes = this.universalActiveSearch.query.company_naics_codes.join(',');
            }
            if (currentPrunedCriteria.company_naics_codes && Array.isArray(currentPrunedCriteria.company_naics_codes)) {
                currentPrunedCriteria.company_naics_codes = currentPrunedCriteria.company_naics_codes.join(',');
            }
            const comparisionPrunedCriteria = this.omitEmptyProps(this.addDefaultAccuracyTier(this.universalActiveSearch.query, this.universalActiveSearch.type));
            return isEqual(omit(this.pruneEmptyValues(JSON.parse(
                JSON.stringify(currentPrunedCriteria))), this.ignoreFieldsArray), omit(this.pruneEmptyValues(
                    JSON.parse(JSON.stringify(comparisionPrunedCriteria))), this.ignoreFieldsArray));
        }
        else {
            return false;
        }
    }

    addDefaultAccuracyTier(query, type) {
        const clonedQuery = cloneDeep(query);
        if (!this.userService.hasAccess(AccountAttributesEnum.ACCURACYTIERS)) {
            return clonedQuery;
        }

        return {
            ...clonedQuery,
            ...(type === 'contact' && !clonedQuery.accuracy_tier_contact ? { accuracy_tier_contact: 'EV' } : {}),
            ...(type === 'company' && !clonedQuery.accuracy_tier_company ? { accuracy_tier_company: 'MV' } : {}),
        };
    }

    omitEmptyProps(obj) {
        return omitBy(obj, function (value) {
            return (typeof value === 'string' && value.trim() === '') ||
                (Array.isArray(value) && value.length === 0);
        });
    }

    pruneEmptyValues(obj) {
        const prunedObject = {};
        forEach(obj, function (value, key) {
            if (Array.isArray(value)) {
                if (value.length > 0) {
                    prunedObject[key] = value;
                }
            } else if (value !== undefined) {
                if (value !== null) {
                    prunedObject[key] = value;
                }
            }
        });
        return prunedObject;
    }

    refreshList() {
        this.refreshSubject.next(Symbol(''));
     }

    clearCurrentSearchIdVisitorIntel() {
        this.currentSearchIdVisitorIntel = null;
    }
}
